/**
 * Typing, and the fact that a page built for a controller is trying to read the same keys.
 *
 * Spatial navigation listens on `window`. Arrow keys move focus, Enter activates whatever holds
 * it, and that is exactly right everywhere on this app except inside a box somebody is writing
 * in — where Enter is a new line, Left is one character back, and moving focus out of the
 * paragraph mid-sentence is the single most annoying thing an editor can do. The same keys mean
 * two different things and the only thing that can tell them apart is whether a text field has
 * the caret.
 *
 * So a field says when it has it. While at least one does, the library is paused: it stops
 * reading keys altogether and every one of them goes where it was typed, untouched. When the
 * last one lets go, it resumes, and the ring is exactly where it was left.
 *
 * Counted rather than a boolean, because focus moves between two fields directly — clicking
 * from the title into the description fires the second field's focus before the first field's
 * blur, and a boolean would come out of that pair switched off with a caret still blinking.
 *
 * The gamepad is not part of this. A pad focused on a field opens the on-screen keyboard rather
 * than typing into it, so nothing here is holding while a stick is being used — which is why B
 * and Escape can safely mean "leave the field" in `AppShell` whenever this is active.
 */

import * as React from 'react';
import { pause, resume } from '@noriginmedia/norigin-spatial-navigation';

let holders = 0;
const listeners = new Set<(active: boolean) => void>();

function announce(): void {
  const active = holders > 0;
  for (const listener of listeners) listener(active);
}

/** True while a caret is in a field and the keys belong to it. */
export function isTextEntryActive(): boolean {
  return holders > 0;
}

export function onTextEntryChange(listener: (active: boolean) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called by a field when it takes the caret. Balanced by exactly one `releaseKeys`. */
export function captureKeys(): void {
  holders += 1;
  if (holders === 1) {
    pause();
    announce();
  }
}

export function releaseKeys(): void {
  if (holders === 0) return;
  holders -= 1;
  if (holders === 0) {
    resume();
    announce();
  }
}

/**
 * Whether this element is somewhere text is typed.
 *
 * Read by the shell to decide what Escape means, and by the field itself when a click lands on
 * something inside its label that is not the input.
 */
export function isTextEntryNode(node: EventTarget | null): boolean {
  const element = node as HTMLElement | null;
  if (!element || typeof element.tagName !== 'string') return false;
  if (element.isContentEditable) return true;
  const tag = element.tagName;
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  // A checkbox and a slider are `input` and neither of them wants the arrow keys held back.
  const type = (element as HTMLInputElement).type;
  return !['checkbox', 'radio', 'range', 'button', 'submit', 'reset', 'file'].includes(type);
}

/**
 * Hands the keys back to the page.
 *
 * Blurring is what does it: the field's own blur handler releases the hold, so there is one
 * path out and no way to end up released with a caret still in a box. Returns whether there was
 * anything to leave, so a caller can fall through to what the key normally does.
 */
export function leaveTextEntry(): boolean {
  if (holders === 0) return false;
  const active = document.activeElement as HTMLElement | null;
  if (active && isTextEntryNode(active)) {
    active.blur();
    return true;
  }
  return false;
}

/**
 * The two handlers a real text field needs, and whether it currently has the caret.
 *
 * Spread onto the `input` or `textarea` itself rather than onto anything around it: these are
 * the DOM's own focus events, and only the element the caret is actually in fires them.
 *
 * The unmount case is the one worth being careful about. A field can be taken off the screen
 * while it still has focus — switching section in the game editor does exactly that — and the
 * browser fires no blur for an element that no longer exists. Without the cleanup, the library
 * would stay paused and the whole app would stop answering the arrow keys.
 */
export function useTextEntryCapture(): {
  capturing: boolean;
  onFocus: () => void;
  onBlur: () => void;
} {
  const [capturing, setCapturing] = React.useState(false);
  // Read by the cleanup, which runs once and would otherwise be holding the first render's
  // answer to whether this field had the caret.
  const held = React.useRef(false);

  React.useEffect(() => {
    return () => {
      if (held.current) {
        held.current = false;
        releaseKeys();
      }
    };
  }, []);

  const onFocus = React.useCallback(() => {
    if (held.current) return;
    held.current = true;
    captureKeys();
    setCapturing(true);
  }, []);

  const onBlur = React.useCallback(() => {
    if (!held.current) return;
    held.current = false;
    releaseKeys();
    setCapturing(false);
  }, []);

  return { capturing, onFocus, onBlur };
}
